import React, { useState, useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  Box,
  Container,
  Heading,
  Text,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  useBreakpointValue,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';

// Import components
import PersentaseTBS from './PersentaseTBS';
import ProduksiPKS from './ProduksiPKS';
import StockCpoPk from './StockCpoPk';
import ProduksiTBS from './ProduksiTBS';
import SortasiPKS from './SortasiPKS';
import PenggunaanSolar from './PenggunaanSolar';
import PengirimanCpoPkCk from './PengirimanCpoPkCk';
import KehadiranPemanen from './KehadiranPemanen';
import TwoRowGrid from './TwoRowGrid';

// Import types
import {
  ProductionTBSData,
  ProductionPKSData,
  StockCpoPkData,
  SortationData,
  SolarUsageData,
  DeliveryData,
  HarvestAttendanceData,
  ApiResponse
} from './types';

// Import configurations
import {
  API_CONFIG,
  DASHBOARD_CONFIG,
  ERROR_MESSAGES,
  LOADING_STATES,
  type LoadingState
} from './config';
import { FiBarChart } from 'react-icons/fi';

const Dashboard: React.FC = () => {
  // State for selected date - default to today
  const [selectedDate, setSelectedDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [loading, setLoading] = useState<LoadingState>(LOADING_STATES.IDLE);

  // State for auto refresh
  const [isAutoRefreshEnabled] = useState<boolean>(true);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  // State for chart data
  const [productionTBSData, setProductionTBSData] = useState<ProductionTBSData[]>([]);
  const [productionPKSData, setProductionPKSData] = useState<ProductionPKSData[]>([]);
  const [stockCpoPkData, setStockCpoPkData] = useState<StockCpoPkData[]>([]);
  const [sortationData, setSortationData] = useState<SortationData[]>([]);
  const [solarUsageData, setSolarUsageData] = useState<SolarUsageData[]>([]);
  const [deliveryData, setDeliveryData] = useState<DeliveryData[]>([]);
  const [harvestAttendanceData, setHarvestAttendanceData] = useState<HarvestAttendanceData[]>([]);

  const textColor = useColorModeValue('gray.800', 'white');

  // Detect if device is mobile
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // Detect if device is tablet (md to lg breakpoints)
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false });
  
  useEffect(() => {
    const handleOrientationChange = () => {
      // Handle orientation changes if needed
    };
    
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  // Fetch data when component mounts and when date changes
  useEffect(() => {
    fetchAllData();
  }, [selectedDate]);

  // Auto refresh effect
  useEffect(() => {
    if (isAutoRefreshEnabled && selectedDate) {
      console.log('Auto refresh dimulai - Interval setiap', DASHBOARD_CONFIG.AUTO_REFRESH_INTERVAL / 60000, 'menit');
      console.log('Tanggal yang dipantau:', selectedDate);

      const interval = setInterval(() => {
        console.log('Auto refresh triggered at:', new Date().toLocaleTimeString());
        fetchAllData();
      }, DASHBOARD_CONFIG.AUTO_REFRESH_INTERVAL);

      setRefreshInterval(interval);

      return () => {
        if (interval) {
          console.log('Auto refresh dihentikan');
          clearInterval(interval);
        }
      };
    } else if (refreshInterval) {
      console.log('Auto refresh dinonaktifkan');
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  }, [isAutoRefreshEnabled, selectedDate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshInterval) {
        console.log('🧹 Cleanup: Auto refresh interval dihentikan saat component unmount');
        clearInterval(refreshInterval);
      }
    };
  }, []);

  const fetchAllData = async () => {
    console.log('Memulai fetch data untuk tanggal:', selectedDate);
    setLoading(LOADING_STATES.LOADING);
    try {
      // Format dates untuk API calls - gunakan selectedDate untuk start dan end
      const endPeriod = selectedDate.replace(/-/g, '');
      const startPeriod = selectedDate.replace(/-/g, ''); // Same as endPeriod
      const datetime = selectedDate.replace(/-/g, '');
      const date = selectedDate.replace(/-/g, '');

      const baseUrl = API_CONFIG.BASE_URL;
      const timeout = API_CONFIG.TIMEOUT;

      console.log('🔗 API endpoints akan dipanggil:', {
        productionTBS: `${baseUrl}${API_CONFIG.ENDPOINTS.PRODUCTION_TBS}?end_period=${endPeriod}`,
        productionPKS: `${baseUrl}${API_CONFIG.ENDPOINTS.PRODUCTION_PKS}?datetime_production=${datetime}`,
        stockCpoPk: `${baseUrl}${API_CONFIG.ENDPOINTS.STOCK_CPO_PK}?datetime_production=${datetime}`,
        sortation: `${baseUrl}${API_CONFIG.ENDPOINTS.PKS_SORTATION}?start_period=${startPeriod}&end_period=${endPeriod}`,
        solarUsage: `${baseUrl}${API_CONFIG.ENDPOINTS.SOLAR_USAGE}?date=${date}`,
        delivery: `${baseUrl}${API_CONFIG.ENDPOINTS.CPO_PK_DELIVERY}?datetime_production=${datetime}`,
        harvestAttendance: `${baseUrl}${API_CONFIG.ENDPOINTS.HARVEST_ATTENDANCE}?company_id=1&selected_date=${date}`
      });

      // Fetch all data in parallel
      const [
        productionTBSResponse,
        productionPKSResponse,
        stockCpoPkResponse,
        sortationResponse,
        solarUsageResponse,
        deliveryResponse,
        harvestAttendanceResponse
      ] = await Promise.all([
        axios.get<ApiResponse<ProductionTBSData>>(`${baseUrl}${API_CONFIG.ENDPOINTS.PRODUCTION_TBS}?end_period=${endPeriod}`, { timeout }),
        axios.get<ApiResponse<ProductionPKSData>>(`${baseUrl}${API_CONFIG.ENDPOINTS.PRODUCTION_PKS}?datetime_production=${datetime}`, { timeout }),
        axios.get<ApiResponse<StockCpoPkData>>(`${baseUrl}${API_CONFIG.ENDPOINTS.STOCK_CPO_PK}?datetime_production=${datetime}`, { timeout }),
        axios.get<ApiResponse<SortationData>>(`${baseUrl}${API_CONFIG.ENDPOINTS.PKS_SORTATION}?start_period=${startPeriod}&end_period=${endPeriod}`, { timeout }),
        axios.get<ApiResponse<SolarUsageData>>(`${baseUrl}${API_CONFIG.ENDPOINTS.SOLAR_USAGE}?date=${date}`, { timeout }),
        axios.get<ApiResponse<DeliveryData>>(`${baseUrl}${API_CONFIG.ENDPOINTS.CPO_PK_DELIVERY}?datetime_production=${datetime}`, { timeout }),
        axios.get<ApiResponse<HarvestAttendanceData>>(`${baseUrl}${API_CONFIG.ENDPOINTS.HARVEST_ATTENDANCE}?company_id=1&selected_date=${date}`, { timeout })
      ]);

      // Set data
      setProductionTBSData(productionTBSResponse.data.data);
      setProductionPKSData(productionPKSResponse.data.data);
      setStockCpoPkData(stockCpoPkResponse.data.data);
      setSortationData(sortationResponse.data.data);
      setSolarUsageData(solarUsageResponse.data.data);
      setDeliveryData(deliveryResponse.data.data);
      setHarvestAttendanceData(harvestAttendanceResponse.data.data);

      console.log('Data berhasil dimuat:', {
        productionTBS: productionTBSResponse.data.data.length,
        productionPKS: productionPKSResponse.data.data.length,
        stockCpoPk: stockCpoPkResponse.data.data.length,
        sortation: sortationResponse.data.data.length,
        solarUsage: solarUsageResponse.data.data.length,
        delivery: deliveryResponse.data.data.length,
        harvestAttendance: harvestAttendanceResponse.data.data.length
      });

      setLoading(LOADING_STATES.SUCCESS);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(LOADING_STATES.ERROR);
    }
  };

  const handleDateChange = (value: string) => {
    console.log('Tanggal diubah dari', selectedDate, 'ke', value);
    setSelectedDate(value);
  };

  // Get today's date for max attribute
  const today = dayjs().format('YYYY-MM-DD');

  // Responsive heights untuk dashboard
  const dashboardHeight = useBreakpointValue({ 
    base: 'auto', 
    md: 'calc(100vh - 80px)', // Laptop
    lg: 'calc(100vh - 90px)',  // Monitor
    xl: '100vh',               // TV/Large monitors
    '2xl': '100vh'
  });

  const containerPadding = useBreakpointValue({ 
    base: 2, 
    md: 2, 
    lg: 2, 
    xl: 2 
  });

  // Responsive font sizes
  const headingSize = useBreakpointValue({ 
    base: 'sm', 
    md: 'md', 
    lg: 'md' 
  });
  const headerPadding = useBreakpointValue({ 
    base: 2, 
    md: 2, 
    lg: 2,
    xl: 2,
    '2xl': 3
  });
  const headerMargin = useBreakpointValue({ 
    base: 2, 
    md: 2, 
    lg: 2,
    xl: 2
  });
  const inputSize = useBreakpointValue({ 
    base: 'xs', 
    md: 'sm' 
  });
  const labelFontSize = useBreakpointValue({ 
    base: 'xs', 
    md: 'sm' 
  });
  const inputWidth = useBreakpointValue({ 
    base: '120px', 
    md: '130px', 
    lg: '140px' 
  });
  const buttonSize = useBreakpointValue({ 
    base: 'xs', 
    md: 'sm' 
  });
  const buttonPadding = useBreakpointValue({ 
    base: 2, 
    md: 3 
  });
  const errorFontSize = useBreakpointValue({ 
    base: 'xs', 
    md: 'sm' 
  });  
  // Responsive grid values
  const gridGap = useBreakpointValue({ 
    base: 1, 
    md: 2, 
    lg: 3 
  });
  const firstRowHeight = useBreakpointValue({ 
    base: '280px', 
    md: '320px', 
    lg: '380px', 
    xl: '310px',
    '2xl': '400px'
  });
  const secondRowHeight = useBreakpointValue({ 
    base: '280px', 
    md: '320px', 
    lg: '380px', 
    xl: '310px',
    '2xl': '400px'
  });

  // Mobile and Tablet Layout
  if (isMobile || isTablet) {
    return (
      <Box minH="100vh" bg="gray.100" p={2} w="100%" overflowY="auto">
        <Container maxW="100%" p={0}>
          {/* Header - Mobile/Tablet version */}
          <Box bg="white" rounded="lg" shadow="md" p={3} mb={3} w="100%">
            <Flex direction="column" gap={3} w="100%">
              <Heading
                as="h1"
                size="sm"
                color="gray.800"
                textAlign="center"
              >
                DASHBOARD HARIAN PRODUKSI
              </Heading>

              {/* Date Filter - Mobile/Tablet */}
              <Flex justify="center" align="center" gap={2} flexWrap="wrap">
                <FormControl w="auto" size="sm">
                  <Flex align="center" gap={2}>
                    <FormLabel
                      htmlFor="date-mobile"
                      fontSize="sm"
                      color="gray.700"
                      mb={0}
                      whiteSpace="nowrap"
                    >
                      Tanggal:
                    </FormLabel>
                    <Input
                      id="date-mobile"
                      type="date"
                      size="sm"
                      value={selectedDate}
                      onChange={(e) => handleDateChange(e.target.value)}
                      max={today}
                      bg="white"
                      w="140px"
                      borderRadius="lg"
                    />
                  </Flex>
                </FormControl>

                <Button
                  onClick={fetchAllData}
                  isLoading={loading === LOADING_STATES.LOADING}
                  colorScheme="blue"
                  size="sm"
                  px={4}
                  fontWeight="medium"
                >
                  Refresh
                </Button>
              </Flex>
            </Flex>

            {/* Error Display - Mobile/Tablet */}
            {loading === LOADING_STATES.ERROR && (
              <Alert status="error" mt={2} rounded="md" size="sm">
                <AlertIcon />
                <AlertTitle fontSize="sm">{ERROR_MESSAGES.LOADING_ERROR}</AlertTitle>
              </Alert>
            )}
          </Box>

          {/* Dashboard Content - Mobile/Tablet */}
          {loading === LOADING_STATES.LOADING ? (
            <Flex justify="center" align="center" h="64">
              <Spinner size="xl" color="blue.500" thickness="4px" />
            </Flex>
          ) : (
            <Box w="100%" display="flex" flexDirection="column" gap={3}>
              {/* Box 1 - Persentase TBS */}
              <Box
                w="100%"
                minH="380px"
                bg="white"
                rounded="lg"
                shadow="md"
                overflow="hidden"
                position="relative"
              >
                {productionTBSData && productionTBSData.length > 0 ? (
                  <PersentaseTBS data={productionTBSData} />
                ) : (
                  <>
                    <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                      PERSENTASE PRODUKSI TBS
                    </Heading>
                    <Flex direction="column" justify="center" align="center" flex="1" gap={3} minH="300px">
                      <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                      <Text color="gray.400" textAlign="center" fontSize="sm">
                        Tidak ada data persentase TBS
                      </Text>
                    </Flex>
                  </>
                )}
              </Box>

              {/* Box 2 - Produksi TBS */}
              <Box
                w="100%"
                minH="380px"
                bg="white"
                rounded="lg"
                shadow="md"
                overflow="hidden"
                position="relative"
              >
                {productionTBSData && productionTBSData.length > 0 ? (
                  <ProduksiTBS data={productionTBSData} />
                ) : (
                  <>
                    <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                      PRODUKSI TBS PER LOKASI
                    </Heading>
                    <Flex direction="column" justify="center" align="center" flex="1" gap={3} minH="300px">
                      <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                      <Text color="gray.400" textAlign="center" fontSize="sm">
                        Tidak ada data Produksi TBS
                      </Text>
                    </Flex>
                  </>
                )}
              </Box>

              {/* Box 3 - Sortasi PKS */}
              <Box
                w="100%"
                minH="380px"
                bg="white"
                rounded="lg"
                shadow="md"
                overflow="hidden"
                position="relative"
              >
                {sortationData && sortationData.length > 0 ? (
                  <SortasiPKS data={sortationData} />
                ) : (
                  <>
                    <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                      SORTASI PKS
                    </Heading>
                    <Flex direction="column" justify="center" align="center" flex="1" gap={3} minH="300px">
                      <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                      <Text color="gray.400" textAlign="center" fontSize="sm">
                        Tidak ada data Sortasi PKS
                      </Text>
                    </Flex>
                  </>
                )}
              </Box>

              {/* Box 4 - Penggunaan Solar */}
              <Box
                w="100%"
                minH="380px"
                bg="white"
                rounded="lg"
                shadow="md"
                overflow="hidden"
                position="relative"
              >
                {solarUsageData && solarUsageData.length > 0 ? (
                  <PenggunaanSolar data={solarUsageData} />
                ) : (
                  <>
                    <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                      PENGGUNAAN SOLAR
                    </Heading>
                    <Flex direction="column" justify="center" align="center" flex="1" gap={3} minH="300px">
                      <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                      <Text color="gray.400" textAlign="center" fontSize="sm">
                        Tidak ada data solar
                      </Text>
                    </Flex>
                  </>
                )}
              </Box>

              {/* Box 5 - Stock CPO/PK */}
              <Box
                w="100%"
                minH="380px"
                bg="white"
                rounded="lg"
                shadow="md"
                overflow="hidden"
              >
                {stockCpoPkData && stockCpoPkData.length > 0 ? (
                  <StockCpoPk data={stockCpoPkData} />
                ) : (
                  <>
                    <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                      STOCK CPO & PK
                    </Heading>
                    <Flex direction="column" justify="center" align="center" flex="1" gap={3} minH="300px">
                      <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                      <Text color="gray.400" textAlign="center" fontSize="sm" whiteSpace="pre-line">
                        {`LHP belum tersedia\n(estimasi H+1 pukul 17:00 WIB)`}
                      </Text>
                    </Flex>
                  </>
                )}
              </Box>

              {/* Box 6 - Produksi PKS */}
              <Box
                w="100%"
                minH="380px"
                bg="white"
                rounded="lg"
                shadow="md"
                overflow="hidden"
                position="relative"
              >
                {productionPKSData && productionPKSData.length > 0 ? (
                  <ProduksiPKS data={productionPKSData} />
                ) : (
                  <>
                    <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                      PRODUKSI PKS
                    </Heading>
                    <Flex direction="column" justify="center" align="center" flex="1" gap={3} minH="300px">
                      <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                      <Text color="gray.400" textAlign="center" fontSize="sm" whiteSpace="pre-line">
                        {`LHP belum tersedia\n(estimasi H+1 pukul 17:00 WIB)`}
                      </Text>
                    </Flex>
                  </>
                )}
              </Box>

              {/* Box 7 - Pengiriman CPO/PK/CK */}
              <Box
                w="100%"
                minH="380px"
                bg="white"
                rounded="lg"
                shadow="md"
                overflow="hidden"
                position="relative"
              >
                {deliveryData && deliveryData.length > 0 ? (
                  <PengirimanCpoPkCk data={deliveryData} />
                ) : (
                  <>
                    <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                      PENGIRIMAN CPO & PK
                    </Heading>
                    <Flex direction="column" justify="center" align="center" flex="1" gap={3} minH="300px">
                      <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                      <Text color="gray.400" textAlign="center" fontSize="sm" whiteSpace="pre-line">
                        {`LHP belum tersedia\n(estimasi H+1 pukul 17:00 WIB)`}
                      </Text>
                    </Flex>
                  </>
                )}
              </Box>

              {/* Box 8 - Kehadiran Pemanen */}
              <Box
                w="100%"
                minH="380px"
                bg="white"
                rounded="lg"
                shadow="md"
                overflow="hidden"
                position="relative"
                mb={4}
              >
                {harvestAttendanceData && harvestAttendanceData.length > 0 ? (
                  <KehadiranPemanen data={harvestAttendanceData} />
                ) : (
                  <>
                    <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                      KEHADIRAN PEMANEN (%)
                    </Heading>
                    <Flex direction="column" justify="center" align="center" flex="1" gap={3} minH="300px">
                      <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                      <Text color="gray.400" textAlign="center" fontSize="sm" whiteSpace="pre-line">
                        Tidak ada data kehadiran pemanen
                      </Text>
                    </Flex>
                  </>
                )}
              </Box>
            </Box>
          )}
        </Container>
      </Box>
    );
  }

  return (
    <Box minH={dashboardHeight} bg="gray.100" p={containerPadding} w="100%">
      <Container bg="gray.100" maxW="100%" minH={dashboardHeight} p={0}>
        <Box bg="white" rounded="lg" shadow="md" p={headerPadding} mb={headerMargin} w="100%">
          <Flex justify="space-between" align="center" w="100%" flexWrap="wrap" gap={2} h="50%">
            <Heading
              as="h1"
              size={headingSize}
              color="gray.800"
              minW="fit-content"
            >
              DASHBOARD HARIAN PRODUKSI
            </Heading>

            {/* Date Filter */}
            <Flex wrap="wrap" gap={2} align="center" minW="fit-content">
              {/* Auto Refresh Toggle */}
              {/* <FormControl w="auto" size={inputSize}>
                <Flex align="center" gap={1}>
                  <FormLabel 
                    htmlFor="auto-refresh" 
                    fontSize={labelFontSize} 
                    color="gray.700" 
                    mb={0} 
                    whiteSpace="nowrap"
                  >
                    Auto Refresh:
                  </FormLabel>
                  <Switch
                    id="auto-refresh"
                    size={inputSize}
                    isChecked={isAutoRefreshEnabled}
                    onChange={toggleAutoRefresh}
                    colorScheme="blue"
                  />
                </Flex>
              </FormControl> */}

              <FormControl w="auto" size={inputSize}>
                <Flex align="center" gap={1} flexWrap="wrap">
                  <FormLabel
                    htmlFor="date"
                    fontSize={labelFontSize}
                    color="gray.700"
                    mb={0}
                    whiteSpace="nowrap"
                  >
                    Tanggal:
                  </FormLabel>
                  <Input
                    id="date"
                    type="date"
                    size={inputSize}
                    value={selectedDate}
                    onChange={(e) => handleDateChange(e.target.value)}
                    max={today}
                    bg="white"
                    w={inputWidth}
                    borderRadius="lg"
                  />
                </Flex>
              </FormControl>

              <Button
                onClick={fetchAllData}
                isLoading={loading === LOADING_STATES.LOADING}
                colorScheme="blue"
                size={buttonSize}
                px={buttonPadding}
                fontWeight="medium"
              >
                Refresh
              </Button>
            </Flex>
          </Flex>

          {/* Error Display */}
          {loading === LOADING_STATES.ERROR && (
            <Alert status="error" mt={1} rounded="md" size="sm">
              <AlertIcon />
              <AlertTitle fontSize={errorFontSize}>{ERROR_MESSAGES.LOADING_ERROR}</AlertTitle>
            </Alert>
          )}
        </Box>

        {/* Dashboard Grid */}
        {loading === LOADING_STATES.LOADING ? (
          <Flex justify="center" align="center" h="64">
            <Spinner size="xl" color="blue.500" thickness="4px" />
          </Flex>
        ) : (
          <TwoRowGrid
            firstRowItems={[
              // Box 1 - Persentase TBS (Pie Chart)
              productionTBSData && productionTBSData.length > 0 ? (
                <PersentaseTBS data={productionTBSData} />
              ) : (
                <Box h="100%" display="flex" flexDirection="column">
                  <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                    PERSENTASE PRODUKSI TBS
                  </Heading>
                  <Flex direction="column" justify="center" align="center" flex="1" gap={3} minH="300px">
                      <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                      <Text color="gray.400" textAlign="center" fontSize="sm" whiteSpace="pre-line">
                        Tidak ada data persentase TBS
                      </Text>
                    </Flex>
                </Box>
              ),
              // Box 2 - Produksi TBS
              productionTBSData && productionTBSData.length > 0 ? (
                <ProduksiTBS data={productionTBSData} />
              ) : (
                <Box h="100%" display="flex" flexDirection="column">
                  <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                    PRODUKSI TBS PER LOKASI
                  </Heading>
                  <Flex direction="column" justify="center" align="center" flex="1" gap={3} minH="300px">
                      <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                      <Text color="gray.400" textAlign="center" fontSize="sm" whiteSpace="pre-line">
                        Tidak ada data Produksi TBS
                      </Text>
                    </Flex>
                </Box>
              ),
              // Box 3 - Sortasi PKS
              sortationData && sortationData.length > 0 ? (
                <SortasiPKS data={sortationData} />
              ) : (
                <Box h="100%" display="flex" flexDirection="column">
                  <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                    SORTASI PKS
                  </Heading>
                  <Flex direction="column" justify="center" align="center" flex="1" gap={3} minH="300px">
                      <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                      <Text color="gray.400" textAlign="center" fontSize="sm" whiteSpace="pre-line">
                        Tidak ada data sortasi PKS
                      </Text>
                    </Flex>
                </Box>
              ),
              // Box 4 - Penggunaan Solar
              solarUsageData && solarUsageData.length > 0 ? (
                <PenggunaanSolar data={solarUsageData} />
              ) : (
                <Box h="100%" display="flex" flexDirection="column">
                  <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                    PENGGUNAAN SOLAR
                  </Heading>
                  <Flex direction="column" justify="center" align="center" flex="1" gap={3} minH="300px">
                      <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                      <Text color="gray.400" textAlign="center" fontSize="sm" whiteSpace="pre-line">
                        Tidak ada data solar
                      </Text>
                    </Flex>
                </Box>
              )
            ]}
            secondRowWidths={['1fr', '1fr', '1fr', '1fr']}
            secondRowItems={[
              // Box 5 - Stock CPO & PK
              stockCpoPkData && stockCpoPkData.length > 0 ? (
                <StockCpoPk data={stockCpoPkData} />
              ) : (
                <Box h="100%" display="flex" flexDirection="column">
                  <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                    STOCK CPO & PK
                  </Heading>
                  <Flex direction="column" justify="center" align="center" flex="1" gap={3} minH="300px">
                      <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                      <Text color="gray.400" textAlign="center" fontSize="sm" whiteSpace="pre-line">
                        {`LHP belum tersedia\n(estimasi H+1 pukul 17:00 WIB)`}
                      </Text>
                    </Flex>
                </Box>
              ),
              // Box 6 - Pengiriman CPO & PK
              deliveryData && deliveryData.length > 0 ? (
                <PengirimanCpoPkCk data={deliveryData} />
              ) : (
                <Box h="100%" display="flex" flexDirection="column">
                  <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                    PENGIRIMAN CPO & PK
                  </Heading>
                  <Flex direction="column" justify="center" align="center" flex="1" gap={3}>
                    <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                    <Text color="gray.400" textAlign="center" fontSize="sm" whiteSpace="pre-line">
                      {`LHP belum tersedia\n(estimasi H+1 pukul 17:00 WIB)`}
                    </Text>
                  </Flex>
                </Box>
              ),
              // Box 7 - Produksi PKS
              productionPKSData && productionPKSData.length > 0 ? (
                <ProduksiPKS data={productionPKSData} />
              ) : (
                <Box h="100%" display="flex" flexDirection="column">
                  <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                    PRODUKSI PKS
                  </Heading>
                  <Flex direction="column" justify="center" align="center" flex="1" gap={3}>
                    <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                    <Text color="gray.400" textAlign="center" fontSize="sm" whiteSpace="pre-line">
                      {`LHP belum tersedia\n(estimasi H+1 pukul 17:00 WIB)`}
                    </Text>
                  </Flex>
                </Box>
              ),
              // Box 8 - Kehadiran Pemanen
              harvestAttendanceData && harvestAttendanceData.length > 0 ? (
                <KehadiranPemanen data={harvestAttendanceData} />
              ) : (
                <Box h="100%" display="flex" flexDirection="column">
                  <Heading as="h3" size="md" mb={2} color={textColor} fontSize="18px" p={4} pb={0}>
                    KEHADIRAN PEMANEN (%)
                  </Heading>
                  <Flex direction="column" justify="center" align="center" flex="1" gap={3}>
                    <Icon as={FiBarChart} boxSize="48px" color="gray.300" />
                    <Text color="gray.400" textAlign="center" fontSize="sm" whiteSpace="pre-line">
                      Tidak ada data kehadiran pemanen
                    </Text>
                  </Flex>
                </Box>
              )
            ]}
            gap={gridGap}
            firstRowHeight={firstRowHeight}
            secondRowHeight={secondRowHeight}
          />
        )}
      </Container>
    </Box>
  );
};

export default Dashboard;
